/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { usePathname, useRouter } from 'next/navigation'

import { setCookie } from 'nookies'

import { queryClient } from '@/api'
import { handleRevalidatePath } from '@/app/api/actions'
import { IApiError } from '@/common/interfaces'
import { cookiesOLP } from '@/cookies/constants'
import { clearDatadogUser, identifyDatadogUser } from '@/plugins/datadog/rum'
import { useTransferInStatementSyncService } from '@/services/account/transferInStatementSync'
import { Login2FAService } from '@/services/auth/login2FAService/login2FAService'
import { IApiLoginErrorResponse } from '@/services/auth/loginService/interfaces/response'
import { LoginService } from '@/services/auth/loginService/loginService'
import { IMultiTenantLogin2FAResponse } from '@/services/auth/multiTenantLogin2FAService/interfaces/response'
import { MultiTenantLogin2FAService } from '@/services/auth/multiTenantLogin2FAService/multiTenantLogin2FAService'
import { GetUserByIdService } from '@/services/user/getUserById/getUserByIdService'
import { routerPaths } from '@/utils/constants/routes'
import { showToast } from '@/utils/toastify/toastify'

import { AuthContext } from './AuthContext'
import {
  IAuthProviderProps,
  Login2FAProps,
  LoginProps,
  ModalVariantType,
  UserType,
} from './types'

export const AuthProvider: FC<IAuthProviderProps> = ({
  children,
  userInformations,
}) => {
  const router = useRouter()
  const pathName = usePathname()

  const [isLoading, setIsLoading] = useState(false)
  const [isLogoutLoading, setIsLogoutLoading] = useState(false)
  const [user, setUser] = useState<UserType>({} as UserType)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [modalVariant, setModalVariant] =
    useState<ModalVariantType>('customMessage')
  const [customMessageError, setCustomMessageError] = useState<
    Array<IApiError> | string
  >([] as Array<IApiError>)

  // Hook para sincronização de transferência em statement
  const isAuthenticated = !!user?.token
  useTransferInStatementSyncService(isAuthenticated)

  useEffect(() => {
    const informations = {} as UserType

    if (userInformations?.token) {
      informations.token = userInformations.token
    }
    if (userInformations?.expiresToken) {
      informations.expiresToken = userInformations.expiresToken
    }
    if (userInformations?.userUuid) {
      informations.userUuid = userInformations.userUuid
    }
    if (userInformations?.companyUuid) {
      informations.companyUuid = userInformations.companyUuid
    }
    if (userInformations?.tenantId) {
      informations.tenantId = userInformations.tenantId
    }

    setUser(informations)

    if (informations.userUuid) {
      identifyDatadogUser({ id: informations.userUuid })
    }
  }, [userInformations])

  const login = useCallback(
    async ({ pwd, userName, rememberMe }: LoginProps) => {
      setIsLogoutLoading(false)
      setIsLoading(true)

      await LoginService({ pwd, userName, userIp: '000.000.000.000' })
        .then(async (response) => {
          if (response) {
            const dataAtual = new Date()

            setCookie(
              null,
              cookiesOLP.olp_token,
              JSON.stringify(response.token),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_expire_token,
              JSON.stringify(response.expirationDate),
              {
                path: '/',
                maxAge: new Date(response.expirationDate),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_user_uuid,
              JSON.stringify(response.userId),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_tenant_id,
              JSON.stringify(response.tenantID),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_remember_me,
              JSON.stringify(rememberMe),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_saved_login_user,
              JSON.stringify(userName),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )

            const userData = await GetUserByIdService({
              id: response.userId,
            })

            setCookie(
              null,
              cookiesOLP.olp_company_uuid,
              JSON.stringify(userData.data.companyId),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_first_access,
              JSON.stringify(userData.data.firstAccess),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )

            setUser({
              companyUuid: userData?.data?.companyId,
              expiresToken: response?.expirationDate,
              tenantId: response?.tenantID,
              token: response?.token,
              userUuid: response?.userId,
            })

            identifyDatadogUser({
              id: response.userId,
              name: userData.data.name,
              email: userData.data.email,
            })

            await handleRevalidatePath({
              route: routerPaths.private.dashboard.default,
              type: 'page',
            })

            if (pathName === routerPaths.public.login.default) {
              router.push(routerPaths.private.dashboard.default)
            }
          }
        })
        .catch((error: IApiLoginErrorResponse) => {
          setCustomMessageError(
            error?.response?.data ?? 'Erro ao tentar fazer login.',
          )
          setModalVariant('customMessage')
          setIsOpenModal(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [router],
  )

  const login2FA = useCallback(
    async ({ pwd, userName, rememberMe, code, tenantId }: Login2FAProps) => {
      setIsLogoutLoading(false)
      setIsLoading(true)

      const loginService = tenantId
        ? MultiTenantLogin2FAService({
            pwd,
            userName,
            userIp: '000.000.000.000',
            code,
            tenantId,
          })
        : Login2FAService({ pwd, userName, userIp: '000.000.000.000', code })

      await loginService
        .then(async (response) => {
          if (response) {
            const dataAtual = new Date()

            setCookie(
              null,
              cookiesOLP.olp_token,
              JSON.stringify(response.token),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_expire_token,
              JSON.stringify(response.expirationDate),
              {
                path: '/',
                maxAge: new Date(response.expirationDate),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_user_uuid,
              JSON.stringify(response.userId),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_tenant_id,
              JSON.stringify(response.tenantID),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_remember_me,
              JSON.stringify(rememberMe),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )
            setCookie(
              null,
              cookiesOLP.olp_saved_login_user,
              JSON.stringify(userName),
              {
                path: '/',
                maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                secure: true,
              },
            )

            if (tenantId) {
              // No fluxo multi-tenant, usa firstAccess do response e ainda busca companyId
              const userData = await GetUserByIdService({ id: response.userId })

              setCookie(
                null,
                cookiesOLP.olp_company_uuid,
                JSON.stringify(userData.data.companyId),
                {
                  path: '/',
                  maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                  secure: true,
                },
              )
              setCookie(
                null,
                cookiesOLP.olp_first_access,
                JSON.stringify(
                  (response as IMultiTenantLogin2FAResponse).firstAccess,
                ),
                {
                  path: '/',
                  maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                  secure: true,
                },
              )

              setUser({
                companyUuid: userData?.data?.companyId,
                expiresToken: response?.expirationDate,
                tenantId: response?.tenantID,
                token: response?.token,
                userUuid: response?.userId,
              })

              identifyDatadogUser({
                id: response.userId,
                name: userData.data.name,
                email: userData.data.email,
              })
            } else {
              // No fluxo não multi-tenant, continua usando GetUserByIdService
              const userData = await GetUserByIdService({ id: response.userId })

              setCookie(
                null,
                cookiesOLP.olp_company_uuid,
                JSON.stringify(userData.data.companyId),
                {
                  path: '/',
                  maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                  secure: true,
                },
              )
              setCookie(
                null,
                cookiesOLP.olp_first_access,
                JSON.stringify(userData.data.firstAccess),
                {
                  path: '/',
                  maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
                  secure: true,
                },
              )

              setUser({
                companyUuid: userData?.data?.companyId,
                expiresToken: response?.expirationDate,
                tenantId: response?.tenantID,
                token: response?.token,
                userUuid: response?.userId,
              })

              identifyDatadogUser({
                id: response.userId,
                name: userData.data.name,
                email: userData.data.email,
              })
            }

            await handleRevalidatePath({
              route: routerPaths.private.dashboard.default,
              type: 'page',
            })

            if (pathName === routerPaths.public.login.default) {
              router.push(routerPaths.private.dashboard.default)
            }
          }
        })
        .catch((error: IApiLoginErrorResponse) => {
          setCustomMessageError(
            error?.response?.data ?? 'Erro ao tentar fazer login.',
          )
          setModalVariant('customMessage')
          setIsOpenModal(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [router, pathName],
  )

  const logout = useCallback(async () => {
    router.prefetch(routerPaths.public.login.default)

    setIsLogoutLoading(true)

    clearDatadogUser()

    // Limpa estado de cliente imediatamente
    queryClient.clear()
    setUser({} as UserType)

    // Dispara limpeza de cookies no servidor em paralelo
    fetch('/api/logout')
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          const cookieValues = Object.values(cookiesOLP)

          const cookieFilered = cookieValues.filter(
            (cookieName) =>
              cookieName !== cookiesOLP.olp_remember_me &&
              cookieName !== cookiesOLP.olp_saved_login_user,
          )

          cookieFilered.forEach((cookie) => {
            document.cookie = `${cookie}=; Path=/; Max-Age=0;`
          })

          router.replace(routerPaths.public.login.default)
        }
      })
      .catch((error) => {
        console.error('Erro ao realizar logout.', error)
      })
      .finally(() => {
        console.log('Safely exited the application.')
        window.location.reload()
        router.replace(routerPaths.public.login.default)
      })
  }, [router])

  useEffect(() => {
    if (user?.expiresToken) {
      const expirationTime = new Date(user.expiresToken).getTime()
      const currentTime = new Date().getTime()

      const timeUntilExpiration = expirationTime - currentTime

      if (timeUntilExpiration <= 0) {
        console.log('!!!!!!!!!!!! - Token expirado. !!!!!!!!!!!!')
        showToast('error', <p>Acesso expirado, faça o login novamente!</p>)
        logout()
      } else {
        const logoutTimeout = setTimeout(() => {
          console.log('!!!!!!!!!!!! - Token expirado. !!!!!!!!!!!!')
          showToast('error', <p>Acesso expirado, faça o login novamente!</p>)
          logout()
        }, timeUntilExpiration)

        return () => clearTimeout(logoutTimeout)
      }
    }
  }, [user?.expiresToken, logout])

  const contextValue = useMemo(() => {
    return {
      isLoading,
      setIsLoading,
      isLogoutLoading,
      isOpenModal,
      setIsOpenModal,
      modalVariant,
      customMessageError,
      setCustomMessageError,
      login,
      login2FA,
      logout,
      user,
      setModalVariant,
    }
  }, [
    isLoading,
    isLogoutLoading,
    isOpenModal,
    modalVariant,
    customMessageError,
    login,
    login2FA,
    logout,
    user,
  ])

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuthProvider = () => useContext(AuthContext)
